import React from "react";
import { useLang } from "./LanguageProvider";
import { Sparkles, Leaf, Award, ShieldCheck } from "lucide-react";

// Sustainability / vision background — neutral hero with Doha skyline at dusk
const VISION_BG =
  "https://images.pexels.com/photos/29676871/pexels-photo-29676871.jpeg?auto=compress&cs=tinysrgb&w=2400";

export default function Vision() {
  const { t, lang, isRtl } = useLang();

  const pillars = [
    {
      icon: Leaf,
      en_tag: "Material",
      ar_tag: "المواد",
      en_title: "Lead-free CPVC",
      ar_title: "CPVC خالٍ من الرصاص",
      en_body: "EPD- and LCA-declared green products. Material safety contributes to LEED & GSAS scoring.",
      ar_body: "منتجات خضراء حاملة لإعلانات EPD و LCA. سلامة المواد تساهم في نقاط LEED و GSAS.",
    },
    {
      icon: Award,
      en_tag: "Lifecycle",
      ar_tag: "العمر التشغيلي",
      en_title: "50+ year service life",
      ar_title: "أكثر من 50 عاماً من الخدمة",
      en_body: "FM- and NSF-tested durability reduces embodied energy and maintenance burden over time.",
      ar_body: "متانة معتمدة FM و NSF تقلل الطاقة المُجسّدة وتكاليف الصيانة على المدى الطويل.",
    },
    {
      icon: ShieldCheck,
      en_tag: "Health",
      ar_tag: "الصحة",
      en_title: "Safe for potable water",
      ar_title: "آمن لمياه الشرب",
      en_body: "NSF/ANSI 61 and WRAS certified for direct contact with drinking water.",
      ar_body: "معتمد NSF/ANSI 61 و WRAS لتلامس مياه الشرب المباشر.",
    },
    {
      icon: Sparkles,
      en_tag: "Local",
      ar_tag: "محلي",
      en_title: "Made in Qatar",
      ar_title: "صُنع في قطر",
      en_body: "100% Qatari-owned manufacturing reduces logistics emissions and supports QNV-2030 self-reliance pillar.",
      ar_body: "تصنيع بملكية قطرية كاملة يقلل انبعاثات النقل ويدعم ركيزة الاعتماد على الذات في رؤية 2030.",
    },
  ];

  return (
    <section
      id="vision"
      data-testid="vision-section"
      className="relative py-24 md:py-40 overflow-hidden bg-brand-navyDarker"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            `linear-gradient(120deg, rgba(4,9,15,0.94) 0%, rgba(10,22,40,0.85) 50%, rgba(10,22,40,0.65) 100%), url(${VISION_BG})`,
        }}
      />
      <div className="absolute inset-0 grain pointer-events-none opacity-90" />

      <div className="relative z-10 container mx-auto px-6 md:px-10">
        {/* Editorial header */}
        <div className="max-w-5xl reveal">
          <div className="flex items-center gap-3 mb-7">
            <span className="h-px w-8 bg-brand-teal" />
            <span className="text-[11px] uppercase tracking-[0.32em] text-brand-teal font-semibold">
              {t.vision.overline}
            </span>
          </div>

          {/* Big editorial pull-quote */}
          <div className="relative pt-12 md:pt-20">
            <span
              className={`absolute top-0 ${isRtl ? "right-0 md:right-2" : "left-0 md:left-2"} font-heading text-brand-teal/20 text-[120px] md:text-[200px] leading-[0.7] select-none pointer-events-none`}
              aria-hidden="true"
            >
              “
            </span>
            <h2 className="relative font-heading text-white text-3xl sm:text-4xl md:text-5xl lg:text-[60px] leading-[1.04] font-medium tracking-tight">
              {t.vision.title}
            </h2>
          </div>

          <div className="mt-8 flex items-center gap-4">
            <span className="h-px w-12 bg-white/30" />
            <span className="text-xs uppercase tracking-[0.28em] text-white/60">
              {isRtl ? "صاحب السمو الأمير الوالد" : "His Highness the Father Emir"}
            </span>
          </div>

          <p className={`reveal reveal-delay-1 mt-12 md:mt-14 text-white/72 text-base md:text-lg lg:text-xl leading-relaxed max-w-3xl ${isRtl ? "text-right" : "text-left"}`}>
            {t.vision.body}
          </p>
        </div>

        {/* Sustainability pillars — editorial vertical stack */}
        <div className="mt-16 md:mt-24 flex flex-col">
          {pillars.map((p, i) => {
            const Icon = p.icon;
            const isLast = i === pillars.length - 1;
            return (
              <div
                key={i}
                data-testid={`vision-pillar-${i}`}
                className={`reveal group grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-9 md:py-12 border-t border-white/10 ${
                  isLast ? "border-b" : ""
                }`}
                style={{ transitionDelay: `${i * 70}ms` }}
              >
                <div className="md:col-span-2 flex md:flex-col items-center md:items-start gap-4 md:gap-3">
                  <span className="w-11 h-11 rounded-full border border-brand-teal/40 bg-brand-teal/10 flex items-center justify-center text-brand-teal flex-shrink-0">
                    <Icon size={18} strokeWidth={1.6} aria-hidden="true" />
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.22em] text-white/45 font-semibold">
                    {lang === "ar" ? p.ar_tag : p.en_tag}
                  </span>
                </div>
                <div className="md:col-span-5">
                  <h3 className="font-heading text-white text-2xl md:text-3xl leading-tight font-medium tracking-tight">
                    {lang === "ar" ? p.ar_title : p.en_title}
                  </h3>
                </div>
                <div className={`md:col-span-5 ${isRtl ? "md:text-right" : ""}`}>
                  <p className="text-white/65 text-base md:text-lg leading-relaxed">
                    {lang === "ar" ? p.ar_body : p.en_body}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Rating system tags */}
        <div className="reveal reveal-delay-2 mt-12 flex flex-wrap items-center gap-3">
          <span className="text-[10px] uppercase tracking-[0.32em] text-white/45 font-semibold">
            {isRtl ? "يساهم في" : "Contributes to"}
          </span>
          {["LEED", "GSAS", "EPD", "LCA", "QCS 2014"].map((label, i) => (
            <span
              key={label}
              data-testid={`vision-tag-${i}`}
              className="inline-flex items-center gap-2 rounded-full border border-brand-teal/40 bg-brand-teal/8 text-brand-teal px-4 py-1.5 text-xs font-semibold tracking-wider"
            >
              {label}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
